import React, { createContext, useReducer } from 'react';
import { connect } from 'react-redux';
import { OPTION_GROUPS_MODALS, PATH_KEY_TO_POSITION } from '../constants';
import { MenuServiceFactory } from '../services/network';
import dotProp from '../utils/dot-prop';
import {
  CREATE_CATEGORY_RESPONSE,
  CREATE_MENU_RESPONSE,
  CREATE_PRODUCT_RESPONSE,
  CREATE_VARIANT_RESPONSE,
  DELETE_CATEGORY_RESPONSE,
  DELETE_MENU_RESPONSE,
  DELETE_PRODUCT_RESPONSE,
  DELETE_VARIANT_RESPONSE,
  UPDATE_CATEGORY_RESPONSE,
  UPDATE_MENU_RESPONSE,
  UPDATE_PRODUCT_RESPONSE,
  UPDATE_VARIANT_RESPONSE,
} from './constants';

export const StateContext = createContext({});
export const DispatchContext = createContext({});

export const initialState = {
  menus: [],
  optionGroups: [],
  loading: false,
  error: null,
  place: null,
  authToken: null,
  expanded: false,
  ogModalMode: OPTION_GROUPS_MODALS.LIST,
  isManageMode: false,
};

export function reducer(state, action) {
  switch (action.type) {
    // TODO: this menu editor state, should just use the component level hooks instead
    // to match the rest of the components
    case 'TOGGLE_MENU_EDITOR': {
      return {
        ...state,
        showMenuEditor: !state.showMenuEditor,
      };
    }
    case 'SET_MENU_EDITOR_ERROR': {
      return { ...state, menuEditorError: action.payload };
    }
    case 'LOADING': {
      return { ...state, loading: action.payload };
    }

    case UPDATE_MENU_RESPONSE: {
      const { menuIdx, menu } = action.payload;
      const updated = {
        ...state,
        loading: false,
      };

      // Add the updated menu
      return dotProp.set(updated, `menus.${menuIdx}`, menu);
    }

    case 'UPDATE_OPTION_GROUPS': {
      const { optionGroups } = action.payload;
      const updated = { ...state };

      return dotProp.set(updated, `optionGroups`, optionGroups);
    }

    case 'TOGGLE_OG_MODAL': {
      const { isOgModalOpen } = action.payload;
      const updated = { ...state, isOgModalOpen };
      return updated;
    }

    case 'TOGGLE_IS_MANAGE_MODE': {
      const { isManageMode } = action.payload;
      const updated = { ...state, isManageMode };
      return updated;
    }

    case 'SET_OG_MODAL_MODE': {
      const { ogModalMode } = action.payload;
      const updated = { ...state, ogModalMode };
      return updated;
    }

    case 'SET_GROUP_TO_EDIT': {
      const { groupToEdit } = action.payload;
      const updated = { ...state, groupToEdit };
      return updated;
    }

    case 'FETCH_MENUS_REQUEST': {
      return {
        ...state,
        loading: true,
      };
    }

    case 'FETCH_MENUS_RESPONSE': {
      return {
        ...state,
        loading: false,
        menus: action.payload,
      };
    }

    case 'FETCH_MENUS_ERROR': {
      return {
        ...state,
        loading: false,
        menus: [],
        error: action.payload,
      };
    }

    case CREATE_MENU_RESPONSE: {
      return {
        menus: [
          ...state.menus,
          { ...action.payload.menu, expanded: action.payload.expanded },
        ],
        loading: false,
      };
    }

    case DELETE_MENU_RESPONSE: {
      const { menuIdx } = action.payload;
      const updated = { ...state, loading: false };
      return dotProp.delete(updated, `menus.${menuIdx}`);
    }

    case CREATE_CATEGORY_RESPONSE: {
      const { menuIdx, category, expanded } = action.payload;
      let updated = { ...state, loading: false };

      // Check if this field exists, if not, use dot prop to create it first
      if (!updated.menus[menuIdx] || !updated.menus[menuIdx].children) {
        updated = dotProp.set(updated, `menus.${menuIdx}.children`, []);
      }

      // Add the new category to the end of the array
      return dotProp.set(updated, `menus.${menuIdx}.children.$push`, {
        ...category,
        expanded,
      });
    }

    case UPDATE_CATEGORY_RESPONSE: {
      const { menuIdx, categoryIdx, category } = action.payload;
      const updated = { ...state, loading: false };
      return dotProp.set(
        updated,
        `menus.${menuIdx}.children.${categoryIdx}`,
        category
      );
    }

    case DELETE_CATEGORY_RESPONSE: {
      const { menuIdx, categoryIdx } = action.payload;
      const updated = { ...state, loading: false };
      return dotProp.delete(
        updated,
        `menus.${menuIdx}.children.${categoryIdx}`
      );
    }

    case UPDATE_PRODUCT_RESPONSE: {
      const { menuIdx, categoryIdx, productIdx, product } = action.payload;
      const updated = { ...state, loading: false };

      // Add the new category to the end of the array
      return dotProp.set(
        updated,
        `menus.${menuIdx}.children.${categoryIdx}.children.${productIdx}`,
        product
      );
    }

    case CREATE_PRODUCT_RESPONSE: {
      const { menuIdx, categoryIdx, product } = action.payload;
      let updated = { ...state, loading: false };

      // Check if this field exists, if not, create and add it
      if (
        !updated.menus[menuIdx].children[categoryIdx] ||
        !updated.menus[menuIdx].children[categoryIdx].children
      ) {
        const path = `menus.${menuIdx}.children.${categoryIdx}.children`;
        updated = dotProp.set(updated, path, []);
      }

      // Add the new product to the end of the list
      return dotProp.set(
        updated,
        `menus.${menuIdx}.children.${categoryIdx}.children.$push`,
        product
      );
    }

    case DELETE_PRODUCT_RESPONSE: {
      const { menuIdx, categoryIdx, productIdx } = action.payload;
      const updated = { ...state, loading: false };
      return dotProp.delete(
        updated,
        `menus.${menuIdx}.children.${categoryIdx}.children.${productIdx}`
      );
    }

    case UPDATE_VARIANT_RESPONSE: {
      const { menuIdx, categoryIdx, productIdx, variantIdx, variant } =
        action.payload;

      const updated = { ...state, loading: false };
      const path = `menus.${menuIdx}.children.${categoryIdx}.children.${productIdx}.children.${variantIdx}`;

      // Add the new category to the end of the array
      return dotProp.set(updated, path, variant);
    }

    case CREATE_VARIANT_RESPONSE: {
      const { menuIdx, categoryIdx, productIdx, variant } = action.payload;
      let updated = { ...state, loading: false };

      // Check if this array exists, if not, create and add it
      if (
        !updated.menus[menuIdx].children[categoryIdx].children[productIdx] ||
        !updated.menus[menuIdx].children[categoryIdx].children[productIdx]
          .children
      ) {
        const path = `menus.${menuIdx}.children.${categoryIdx}.children.${productIdx}.children`;
        updated = dotProp.set(updated, path, []);
      }

      // Add the new variant and return the new state
      const prop = `menus.${menuIdx}.children.${categoryIdx}.children.${productIdx}.children.$push`;
      return dotProp.set(updated, prop, variant);
    }

    case DELETE_VARIANT_RESPONSE: {
      const { menuIdx, categoryIdx, productIdx, variantIdx } = action.payload;
      const updated = { ...state, loading: false };
      const path = `menus.${menuIdx}.children.${categoryIdx}.children.${productIdx}.children.${variantIdx}`;
      return dotProp.delete(updated, path);
    }

    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function pathToIndices(path) {
  let indices = [];

  for (const p of path) {
    indices[PATH_KEY_TO_POSITION[p.key]] = p.value;
  }

  return indices;
}

function itemFromIndices(state, indices) {
  const [menuIdx, ...childIndices] = indices;

  const key = childIndices.reduce((p, idx) => {
    return p.concat(`.children.${idx}`);
  }, `menus.${menuIdx}`);

  return dotProp.get(state, key);
}

// assuming new items are always appended and updates cannot be made that could
// cause duplicate positions
function positionFromPath(state, path) {
  const prev = previousItemFromPath(state, path);

  if (prev === undefined || prev.position === undefined) {
    return 0;
  }

  return prev.position + 1;
}

function previousItemFromPath(state, path) {
  const indices = pathToIndices(path);
  const lastIdx = indices[indices.length - 1];

  if (lastIdx === undefined) {
    if (indices.length === 1) {
      return state.menus[state.menus.length - 1];
    }

    const parent = itemFromIndices(state, indices.slice(0, -1));

    if (parent === undefined || !parent.children) {
      return;
    }

    return parent.children[parent.children.length - 1];
  }

  if (lastIdx === 0) {
    return;
  }

  return itemFromIndices(state, [...indices.slice(0, -1), lastIdx - 1]);
}

function MenuEditorContext({ children, authToken, place }) {
  // Ensure the place id and auth token are set against the reducer
  initialState.place = place;
  initialState.authToken = authToken;

  const [state, dispatch] = useReducer(reducer, initialState);

  const getState = path => itemFromIndices(state, pathToIndices(path));
  const calculatePosition = path => positionFromPath(state, path);

  // Wraps around the network service for the menu service
  // TODO: Joey mentioned there already being a network/auth
  // set-up, and a global reducer, we should look at using that
  // instead. `useReducer` is more modular code wise, but
  // should look to use one or the other, rather than having both.
  const client = MenuServiceFactory(authToken, place);

  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider
        value={{ state, client, getState, calculatePosition }}
      >
        {children}
      </StateContext.Provider>
    </DispatchContext.Provider>
  );
}

const mapStateToProps = state => ({
  place: state.venue.defaultVenue,
  authToken: state.authentication.accessToken,
});

export default connect(mapStateToProps)(MenuEditorContext);



// WEBPACK FOOTER //
// ./src/components/Dashboard/Panels/MenuEditor/contexts/use-menu-editor-context.jsx